import "./roomtypeslist.css";
import { ToggleButton } from "../../BasicComponent";
import { useEffect, useMemo, useState } from "react";
import API from "src/API2";
import toast from "react-hot-toast";

/* ── Inline icons ───────────────────────────────────────────────── */
const Svg = ({ children, size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">{children}</svg>
);
const IcUser    = () => <Svg size={14}><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" /></Svg>;
const IcKid     = () => <Svg size={14}><circle cx="12" cy="7" r="3" /><path d="M5 21v-2a4 4 0 0 1 4-4h6a4 4 0 0 1 4 4v2" /></Svg>;
const IcGrid    = () => <Svg size={14}><rect x="3" y="3" width="7" height="7" /><rect x="14" y="3" width="7" height="7" /><rect x="14" y="14" width="7" height="7" /><rect x="3" y="14" width="7" height="7" /></Svg>;
const IcRuler   = () => <Svg size={14}><path d="M3 21l18-18" /><path d="M5 19l3-3M9 15l3-3M13 11l3-3" /></Svg>;
const IcEdit    = () => <Svg size={14}><path d="M12 20h9" /><path d="M16.5 3.5a2.121 2.121 0 1 1 3 3L7 19l-4 1 1-4 12.5-12.5z" /></Svg>;
const IcTrash   = () => <Svg size={14}><polyline points="3 6 5 6 21 6" /><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" /><path d="M10 11v6M14 11v6" /><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" /></Svg>;
const IcEmpty   = () => <Svg size={32}><path d="M2 20v-8a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v8" /><path d="M2 17h20" /><path d="M6 10V6a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v4" /></Svg>;

const RoomTypesList = ({ searchQuery = "" }) => {
  const [rooms, setRooms] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { fetchRooms(); }, []);

  const fetchRooms = async () => {
    setLoading(true);
    try {
      const response = await API.get("rooms");
      if (response.data?.rooms) setRooms(response.data.rooms);
      else { setRooms([]); toast.error("Received invalid room data format"); }
    } catch (err) {
      console.error("API error:", err);
      setRooms([]);
      toast.error("Failed to fetch rooms.");
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = (id, newState) => {
    setRooms((prev) => prev.map((r) => (r.id === id ? { ...r, available: newState } : r)));
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this room type? This cannot be undone.")) return;
    try {
      await API.delete(`rooms/${id}`);
      setRooms((prev) => prev.filter((r) => r.id !== id));
      toast.success("Room type deleted");
    } catch {
      toast.error("Failed to delete room type");
    }
  };

  const filtered = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return rooms;
    return rooms.filter(
      (r) =>
        r?.code?.toLowerCase().includes(q) ||
        r?.description?.toLowerCase().includes(q),
    );
  }, [rooms, searchQuery]);

  /* ── Skeleton ── */
  if (loading) {
    return (
      <div className="rt-grid">
        {Array.from({ length: 6 }).map((_, i) => (
          <div key={i} className="rt-card rt-card--skeleton">
            <div className="rt-skel rt-skel--head" />
            <div className="rt-skel rt-skel--line" />
            <div className="rt-skel rt-skel--line rt-skel--short" />
            <div className="rt-card__stats">
              <div className="rt-skel rt-skel--block" />
              <div className="rt-skel rt-skel--block" />
              <div className="rt-skel rt-skel--block" />
              <div className="rt-skel rt-skel--block" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  /* ── Empty (initial) ── */
  if (rooms.length === 0) {
    return (
      <div className="rt-empty">
        <IcEmpty />
        <h3>No room types yet</h3>
        <p>Create your first room type to start selling rooms.</p>
      </div>
    );
  }

  /* ── Grid ── */
  return (
    <>
      <div className="rt-grid">
        {filtered.map((room) => (
          <article key={room.id} className={`rt-card ${room.available ? "" : "is-unavailable"}`}>
            <header className="rt-card__head">
              <div className="rt-card__head-text">
                <span className="rt-card__code">{room.code}</span>
                <span className={`rt-status rt-status--${room.available ? "on" : "off"}`}>
                  {room.available ? "Available" : "Off market"}
                </span>
              </div>
              <ToggleButton
                state={room.available}
                onChange={(s) => handleToggle(room.id, s)}
                id={room.id}
              />
            </header>

            <p className="rt-card__desc">{room.description || "No description provided."}</p>

            <div className="rt-card__stats">
              <div className="rt-stat">
                <span className="rt-stat__icon"><IcUser /></span>
                <span className="rt-stat__value">{room.max_adults || 0}</span>
                <span className="rt-stat__label">Adults</span>
              </div>
              <div className="rt-stat">
                <span className="rt-stat__icon"><IcKid /></span>
                <span className="rt-stat__value">{room.max_children || 0}</span>
                <span className="rt-stat__label">Children</span>
              </div>
              <div className="rt-stat">
                <span className="rt-stat__icon"><IcGrid /></span>
                <span className="rt-stat__value">{room.count || 0}</span>
                <span className="rt-stat__label">Rooms</span>
              </div>
              <div className="rt-stat">
                <span className="rt-stat__icon"><IcRuler /></span>
                <span className="rt-stat__value">{room.size || 0}m²</span>
                <span className="rt-stat__label">Size</span>
              </div>
            </div>

            <div className="rt-card__tags">
              <span className={`rt-tag ${room.refundable ? "rt-tag--green" : "rt-tag--gray"}`}>
                {room.refundable ? "Refundable" : "Non-refundable"}
              </span>
            </div>

            <footer className="rt-card__foot">
              <button
                className="rt-btn rt-btn--ghost"
                onClick={() => (window.location.href = `${window.location.pathname}/update/${room.id}`)}
              >
                <IcEdit /> Edit
              </button>
              <button className="rt-btn rt-btn--danger-ghost" onClick={() => handleDelete(room.id)}>
                <IcTrash /> Delete
              </button>
            </footer>
          </article>
        ))}
      </div>

      {filtered.length === 0 && (
        <div className="rt-empty rt-empty--inline">
          <IcEmpty />
          <h3>No matches</h3>
          <p>No room types match "{searchQuery}".</p>
        </div>
      )}
    </>
  );
};

export default RoomTypesList;
